import {
  forecastPreviewQuery,
  useCreateForecastMutation,
} from "@common/api/forecast"
import { Button } from "@common/components/ui/button"
import { useHasPermission } from "@common/hooks/use-permissions"
import { cn } from "@common/lib/utils"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { Eye } from "lucide-react"
import useCustomToast from "@/hooks/use-custom-toast"
import { requireForecastPages } from "@/routes/_layout/forecasts/-lib/gate"
import { DueList } from "./-components/due-list"
import { COLUMNS } from "./-lib/columns"
import { FILTER_KEYS } from "./-lib/filters"
import {
  GROUPINGS,
  type PreviewSearch,
  previewParameters,
  previewSearchSchema,
} from "./-lib/search"
import { useUpdateSearch } from "./-lib/use-update-search"

const BANNER = cn(
  "flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-2",
  "bg-semantic-info-soft text-body-medium",
)

export const Route = createFileRoute("/_layout/forecasts_/preview")({
  validateSearch: (search) => previewSearchSchema.parse(search),
  loaderDeps: ({ search: { aircraft_ids, starts_on, ends_on, basis } }) => ({
    aircraft_ids,
    starts_on,
    ends_on,
    basis,
  }),
  beforeLoad: ({ context: { queryClient } }) =>
    requireForecastPages(queryClient),
  loader: async ({ context: { queryClient }, deps }) => {
    await queryClient.ensureQueryData(
      forecastPreviewQuery({ forecastParameters: previewParameters(deps) }),
    )
    return {
      breadcrumb: [
        { label: "Forecast", href: "/forecasts" },
        { label: "Preview" },
      ],
    }
  },
  component: PreviewPage,
})

function PreviewPage(): React.ReactElement {
  const search = Route.useSearch()
  const update = useUpdateSearch()
  const parameters = previewParameters(search)
  const { data: result } = useSuspenseQuery(
    forecastPreviewQuery({ forecastParameters: parameters }),
  )

  return (
    <DueList
      result={result}
      search={search}
      columns={COLUMNS}
      groupings={GROUPINGS}
      filterKeys={FILTER_KEYS}
      basis={parameters.basis}
      banner={<PreviewBanner search={search} />}
      onSearch={update}
      onBasis={(basis) => update({ basis })}
    />
  )
}

function PreviewBanner({
  search,
}: {
  search: PreviewSearch
}): React.ReactElement {
  const canCreate = useHasPermission("forecast:create")
  const mutation = useCreateForecastMutation()
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const save = () =>
    mutation.mutate(
      {
        forecastCreate: {
          ...previewParameters(search),
          name: search.name,
          note: search.note || null,
        },
      },
      {
        onSuccess: (created) => {
          showToast(`Saved ${created.number}`, created.name, "success")
          navigate({
            to: "/forecasts/$forecastId",
            params: { forecastId: created.id },
          })
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <div className={BANNER}>
      <Eye className="size-4 text-semantic-info" />
      <span>
        {"Previewing "}
        <span>{search.name}</span>
        <span className="text-semantic-info"> · not saved yet</span>
      </span>
      <span className="ml-auto flex gap-2">
        <Button asChild variant="outline" size="sm">
          <Link to="/forecasts">Back to forecasts</Link>
        </Button>
        {canCreate && (
          <Button size="sm" disabled={mutation.isPending} onClick={save}>
            {mutation.isPending ? "Saving…" : "Save forecast"}
          </Button>
        )}
      </span>
    </div>
  )
}
